(ns frontend.view.reports.view
  (:require
   [clojure.string :as string]
   [common.localization :as loc]
   [common.sync-progress :as sync-progress]
   [frontend.conn :as conn]
   [frontend.db :as db]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.tenant-customizations :as tenant-customizations]
   [frontend.ui.component.date-range-picker.view :as date-range-picker]
   [frontend.utils :as fu]
   [frontend.view.reports.all.view :as all-reports]
   [frontend.view.reports.components.side-menu :as side-menu]
   [frontend.view.reports.custom.alj-2.utils :as alj-utils]
   [frontend.view.reports.custom.view :as custom-report]
   [frontend.view.reports.custom.view-2 :as custom-report-2]
   [frontend.view.reports.custom.view-3 :as custom-report-3]
   [frontend.view.reports.util :as reports-util]
   [frontend.view.syncing-data :as syncing-data]
   [ui.components.toggle.view :as toggle]

   [reagent.core :as r]))

(defn toolbar-buttons
  [state reloading?]
  [{:toolbar-button?  true
    :dropdown-action? true
    :ord              1
    :icon             (if reloading? "fa-sync fa-pulse" :fa-sync)
    :label            (if reloading? (loc/tr :reloading) (loc/tr :refresh))
    :primary-button?  true
    :on-select        (fn [_e]
                        (swap! state assoc :reloading true)
                        (conn/send! :report/recalculate-all-reports
                                    {}
                                    (fn [resp]
                                      (let [job-id (:body resp)]
                                        (session/wait-for-entity-attribute-value! job-id
                                                                                  :job/status
                                                                                  [:job.status/finished
                                                                                   :job.status/error]
                                                                                  (fn [_]
                                                                                    (swap! state assoc
                                                                                           :reloading
                                                                                           false)))))))}])

(defn toggle-show-disabled-users
  [{:keys [state]}]
  [toggle/toggle
   {:checked?      (:show-disabled-users? @state) 
    :on-change   (fn [toggled?]
                     (swap! state assoc :show-disabled-users? toggled?))}])

(defn tool-date-range
  [date-state label]
  [:div
   {:class "flex flex-col"}
   [:label
    {:class "text-xs"}
    label]
   [date-range-picker/date-range-dropdown
    {:date-state-atom  date-state
     :hide-label?      true
     :on-change        identity
     :static-ranges-kw :alj}]])

(defn view
  [{:keys [state]} & _children]
  (let [toolbar-state (r/atom {})
        date-created-state (r/cursor state [:date-created])
        date-passed-state (r/cursor state [:date-passed])
        date-portfolio-state (r/cursor state [:date-portfolio])
        date-cic-state (r/cursor state [:date-cic])]
    (r/create-class
     {:reagent-render
      (fn [{:keys [view-key title state hide-side-menu?]} & children]
        (let [available (not (session/is-guest?))
              smallest-report-ts (->> @session/all-cached-reports
                                      (filter
                                       (fn [cr]
                                         (some #{(:cached-report/report-key cr)}
                                               [:cached-report.type/new-deals-per-account
                                                :cached-report.type/new-deals-per-owner
                                                :cached-report.type/new-deals-per-lead-source
                                                :cached-report.type/new-deals-per-lead-type
                                                :cached-report.type/new-deals-per-funding-round])))
                                      (sort-by :common/updated-on)
                                      (last)
                                      :common/updated-on)
              main-reports? (or (= title (loc/tr :reports/new-deals))
                                (= view-key :main-reports))]
          (if-not available
            [:div (loc/tr :not-allowed)]
            [:<>
             [zap-toolbar/view db/session (partial zap-toolbar/back-label title) nil
              (cond main-reports?
                    (toolbar-buttons toolbar-state (:reloading @toolbar-state))
                    :else
                    [])
              (cond
                main-reports?
                (fn []
                  [:div.d-flex.align-items-center.pr-1
                   [:span.last-updated.text-size--sm.text-color--light-grey.text-transform-initial.white-space-nowrap.mr-4
                    (loc/tr :last-updated-on [(session/long->tenant-date-time smallest-report-ts)])]
                   [:span.d-inline-flex.align-items-center
                    [toggle-show-disabled-users {:state state}]
                    [:span.d-flex.input-editor__label.text-regular.white-space-nowrap.ml-2
                     (loc/tr :show-deactivated-users)]]])
                (and (= view-key :custom-reports-2)
                     (tenant-customizations/alj?))
                (fn []
                  [:div
                   {:class "flex gap-2"}
                   [tool-date-range date-created-state (loc/tr :common/created-on)]
                   [tool-date-range date-passed-state (loc/tr :oppo/passed-on)]
                   [tool-date-range date-portfolio-state (loc/tr :oppo/moved-to-portfolio-on)]
                   [tool-date-range date-cic-state (loc/tr :oppo/date-CIC-presentation)]]
                  ))]

             [:div.entity__container
              (when-not hide-side-menu?
                [:div.reports-view__side-menu
                 [side-menu/view view-key]])
              [:div
               {:class-name "px-4"}
               children]]])))})))

(defn custom-reports-view
  [state]
  (let [id (str "custom-reports-2"
                (->> [:date-created :date-passed :date-portfolio :date-cic]
                     (map (fn [d]
                            (str (get-in @state [d :start-ts])
                                 "-"
                                 (get-in @state [d :end-ts]))))
                     (string/join "-")))]
    ^{:key id}
    [default-page-layout/view
     [view
      {:view-key        :custom-reports
       :hide-side-menu? true
       :state           state
       :title           (reports-util/report-title :custom-reports)}
      ^{:key "custom-reports"}
      [syncing-data/wait-until-sync-progress sync-progress/COMPLETE
       [custom-report/custom-view true]]]]))

(defmethod routing/render-view :custom-reports
  [_]
  (let [default-date-state (alj-utils/default-date-range-state nil)
        state (r/atom {:date-created   default-date-state
                       :date-passed    default-date-state
                       :date-portfolio default-date-state
                       :date-cic       default-date-state})]
    [custom-reports-view state]))

(defn custom-reports-2
  [state]
  (let [id (str "custom-reports-2"
                (->> [:date-created :date-passed :date-portfolio :date-cic]
                     (map (fn [d]
                            (str (get-in @state [d :start-ts])
                                 "-"
                                 (get-in @state [d :end-ts]))))
                     (string/join "-")))]
    ^{:key id}
    [default-page-layout/view
     [view
      {:view-key        :custom-reports-2
       :hide-side-menu? true
       :state           state
       :title           (reports-util/report-title :custom-reports-2)}
      [syncing-data/wait-until-sync-progress sync-progress/COMPLETE
       [custom-report-2/custom-view
        {:state state}]]]]))

(defmethod routing/render-view :custom-reports-2
  [_]
  (let [default-date-state (alj-utils/default-date-range-state nil)
        state (r/atom {:date-created   default-date-state
                       :date-passed    default-date-state
                       :date-portfolio default-date-state
                       :date-cic       default-date-state})]
    [custom-reports-2 state]))

(defn custom-reports-3-view
  [state]
  (let [id (str "custom-reports-2"
                (->> [:date-created :date-passed :date-portfolio :date-cic]
                     (map (fn [d]
                            (str (get-in @state [d :start-ts])
                                 "-"
                                 (get-in @state [d :end-ts]))))
                     (string/join "-")))]
    ^{:key id}
    [default-page-layout/view
     [view
      {:view-key        :custom-reports-3
       :hide-side-menu? true
       :state           state
       :title           (reports-util/report-title :custom-reports-3)}
      ^{:key "custom-reports-e"}
      [syncing-data/wait-until-sync-progress sync-progress/COMPLETE
       [custom-report-3/custom-view true]]]]))

(defmethod routing/render-view :custom-reports-3
  [_state]
  (let [default-date-state (alj-utils/default-date-range-state nil)
        state (r/atom {:date-created   default-date-state
                       :date-passed    default-date-state
                       :date-portfolio default-date-state
                       :date-cic       default-date-state})]
    [custom-reports-3-view state]))

(defn all-reports-view
  []
  (let [state (r/atom {})]
    (fn []
      [view
       {:state           state
        :view-key        :main-reports
        :hide-side-menu? true
        :title           (loc/tr :reports)}
       ^{:key "all-reports"}
       [all-reports/view {:state state}]])))

(defmethod routing/render-view :main-reports
  [_]
  [default-page-layout/view
   {:class-name (fu/cs "zap-page reports")}
   [all-reports-view]])
